import { FormEvent, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { api, ApiError } from "../lib/api";

// Public sign-in page for SaaS deploys (deploy_mode=saas).
//
// Submitting the form POSTs to /api/v1/auth/request-link. Regardless of
// whether the email is recognised, the backend always returns 204 — we
// then render the "check your email" pane so we don't leak which addresses
// have accounts. The actual sign-in happens when the user clicks the link
// in their inbox, which lands on /api/v1/auth/verify (backend route), which
// sets an HttpOnly session cookie and 303s back to /admin.
//
// On verify failure (expired/already-used link) the backend redirects here
// with ?error=expired so we can render a banner above the form.
export default function Login() {
  const [params] = useSearchParams();
  const verifyError = params.get("error");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [authMode, setAuthMode] = useState<"header" | "magic_link" | null>(
    null,
  );

  // Probe /api/v1/config so we can tell the user upfront when this deploy
  // doesn't support magic-link sign-in (cluster mode). Without this hint
  // the form would silently 404 on submit, which is unfriendly.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await api.config();
        if (!cancelled) setAuthMode(c.auth_mode);
      } catch {
        // /api/v1/config is always present; if it 5xx's we just leave the
        // form in its default state. Don't block sign-in on the probe.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      setErr("Enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await api.requestMagicLink(trimmed);
      setSent(true);
    } catch (e2) {
      setErr(
        e2 instanceof ApiError
          ? e2.message
          : "Could not send sign-in link. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <main className="reading">
        <h1>Check your email</h1>
        <p>
          We sent a sign-in link to <strong>{email}</strong>. Click the
          link to finish signing in. The link expires in 15 minutes and
          can only be used once.
        </p>
        <p className="meta">
          Didn't get it? Check your spam folder, then{" "}
          <button
            type="button"
            className="link"
            onClick={() => {
              setSent(false);
              setEmail("");
            }}
          >
            try again
          </button>
          .
        </p>
      </main>
    );
  }

  return (
    <main className="reading">
      <h1>Sign in to AskTogether</h1>
      {verifyError === "expired" && (
        <p className="error" role="alert">
          That sign-in link has expired or was already used. Enter your
          email below and we'll send a fresh one.
        </p>
      )}
      {authMode === "header" && (
        <p className="meta" role="status">
          This deployment uses OAuth at the network edge, not magic-link
          sign-in. If you're an organiser, refresh the page to be sent
          through the OAuth flow instead.
        </p>
      )}
      <form onSubmit={onSubmit} aria-label="Sign in">
        <label htmlFor="login-email">Email address</label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          disabled={submitting}
        />
        <button type="submit" disabled={submitting || !email.trim()}>
          {submitting ? "Sending…" : "Send sign-in link"}
        </button>
      </form>
      {err && (
        <p className="error" role="alert">
          {err}
        </p>
      )}
      <p className="meta">
        We'll email you a one-tap link. First time? Signing in creates a
        free AskTogether organisation for you.
      </p>
    </main>
  );
}
